import { useState } from "react";
import { X, Maximize2, Minimize2, Phone, Video, ExternalLink, Loader } from "lucide-react";

interface VideoRoomProps {
  roomName?: string;
  displayName?: string;
  onClose?: () => void;
  meetingLink?: string;
  platform?: "zoom" | "jitsi" | "auto";
}

const VideoRoom = ({ 
  roomName, 
  displayName = "User", 
  onClose, 
  meetingLink,
  platform = "auto"
}: VideoRoomProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Determine the video URL based on meetingLink or platform
  const getVideoUrl = () => {
    // If meetingLink is provided, use it directly (Zoom or any other)
    if (meetingLink) {
      return meetingLink;
    }
    
    // If platform is explicitly set to jitsi, use Jitsi
    if (platform === "jitsi") {
      return `https://meet.jit.si/${roomName}#config.prejoinPageEnabled=false&config.disableDeepLinking=true&userInfo.displayName=${encodeURIComponent(displayName)}`;
    }
    
    // For Zoom platform without meetingLink, we can't construct a valid URL
    if (platform === "zoom") {
      return null;
    }
    
    // Auto mode: default to Jitsi
    return `https://meet.jit.si/${roomName}#config.prejoinPageEnabled=false&config.disableDeepLinking=true&userInfo.displayName=${encodeURIComponent(displayName)}`;
  };

  const videoUrl = getVideoUrl();

  // Check if it's a Zoom meeting
  const isZoomMeeting = meetingLink && meetingLink.includes("zoom.us");

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const openInNewTab = () => {
    if (meetingLink) {
      window.open(meetingLink, "_blank");
    } else {
      window.open(`https://meet.jit.si/${roomName}`, "_blank");
    }
  };

  const hangUp = () => {
    onClose?.();
  };

  // Show error if no valid meeting URL
  if (!videoUrl) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Video className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <p className="text-white font-medium mb-2">No meeting link available</p>
          <p className="text-sm text-gray-400 mb-4">
            This session does not have an active meeting link.
          </p>
          <button
            onClick={onClose || (() => window.history.back())}
            className="flex items-center gap-2 mx-auto px-4 py-2 text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // For Zoom meetings, show a landing page with button to open in new tab
  if (isZoomMeeting) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-white">Zoom Meeting</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(meetingLink, "_blank")}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Open Zoom Meeting
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
              <Video className="w-12 h-12 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Zoom Meeting</h2>
            <p className="text-gray-400 mb-6 max-w-md">
              Your meeting is ready. Click the button above to join the Zoom session in a new tab.
            </p>
            <button
              onClick={() => window.open(meetingLink, "_blank")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
            >
              <ExternalLink className="w-5 h-5" />
              Join Meeting in New Tab
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Regular Jitsi embed for non-Zoom meetings
  const jitsiUrl = videoUrl;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium text-white">Live Session</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleFullscreen}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={openInNewTab}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            title="Open in new tab"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
          <button
            onClick={hangUp}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Phone className="w-4 h-4 rotate-[135deg]" />
            Leave
          </button>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 relative">
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-900">
            <div className="text-center">
              <Loader className="w-8 h-8 mx-auto mb-4 text-primary animate-spin" />
              <p className="text-sm text-gray-400">Connecting...</p>
            </div>
          </div>
        )}
        <iframe
          src={jitsiUrl}
          className="w-full h-full border-0"
          allow="camera; microphone; fullscreen; display-capture; autoplay"
          allowFullScreen
          title="Video Call"
          onLoad={() => setIsLoading(false)}
        />
      </div>
    </div>
  );
};

export default VideoRoom;
